import { SOCIETY_SETTING_TYPE } from '@/constants/societySettings';
import type { EbvConfigItem } from '@/hooks/publicSearch/ebv/ebvColumns';

export type TraitExplanationEntry = {
  title: string;
  description: string;
  tipSheetUrl?: string;
};

export type EbvTraitHeaderContext = {
  traitExplanationsByKey: Map<string, TraitExplanationEntry>;
  onTraitInfoClick: (
    event: React.MouseEvent<HTMLElement>,
    entry: TraitExplanationEntry,
  ) => void;
};

type SocietySettingLike = {
  societySettingType?: string | null;
  value?: string | null;
};

/** Shared (i) size for EBV trait headers in grids, tables, and charts. */
export const EBV_TRAIT_INFO_ICON_SIZE = 13;

/** Plain-text label for tooltips / aria (no caret line breaks). */
export function formatEbvLabelPlain(label: string): string {
  return label.replace(/\^/g, ' ').replace(/\s+/g, ' ').trim();
}

export function getEbvConfigTraitKey(
  config: Pick<EbvConfigItem, 'traitKey' | 'TraitKey'>,
): string | undefined {
  const key = config.traitKey ?? config.TraitKey;
  if (key == null) return undefined;
  const trimmed = String(key).trim();
  return trimmed || undefined;
}

export function parseEbvTraitExplanations(
  settings?: SocietySettingLike[] | null,
): Map<string, TraitExplanationEntry> {
  const map = new Map<string, TraitExplanationEntry>();

  const resolveTipSheetUrl = (entry: Record<string, unknown>) => {
    if (entry?.tipSheetUrl != null) return String(entry.tipSheetUrl);
    if (entry?.TipSheetUrl != null) return String(entry.TipSheetUrl);
    return undefined;
  };

  const addEntry = (entry: Record<string, unknown>) => {
    const traitKey =
      entry?.traitKey ?? entry?.TraitKey ?? entry?.ebvType ?? entry?.EbvType;
    const title =
      entry?.title ?? entry?.Title ?? entry?.ebvName ?? entry?.EbvName;
    const description = entry?.description ?? entry?.Description;
    if (!traitKey || title == null || description == null) return;
    map.set(String(traitKey).trim(), {
      title: String(title),
      description: String(description),
      tipSheetUrl: resolveTipSheetUrl(entry),
    });
  };

  if (!settings || !Array.isArray(settings)) {
    return map;
  }

  const setting = settings.find(
    (setting) =>
      setting.societySettingType === SOCIETY_SETTING_TYPE.EBV_DESCRIPTION,
  );
  if (setting?.value) {
    try {
      const parsed = JSON.parse(setting.value);
      if (Array.isArray(parsed)) {
        parsed.forEach(addEntry);
      } else if (
        parsed &&
        typeof parsed === 'object' &&
        Array.isArray(parsed.traits)
      ) {
        parsed.traits.forEach(addEntry);
      }
    } catch {
      // ignore invalid JSON
    }
  }

  return map;
}
